import { useEffect, useRef, useState } from 'react';
import { useConsent } from '../utils/consent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Pojedynczy slot AdSense.
 *
 * Renderuje <ins class="adsbygoogle"> tylko gdy:
 *  - uzytkownik wyrazil zgode na reklamy (consent.ads === true)
 *  - admin wlaczyl globalnie reklamy (adsense.enabled_global)
 *  - dany slot ma "enabled: true" i "id" w ustawieniach
 *  - client_id ustawiony
 *
 * Props:
 *  - name: 'header' | 'in_article' | 'sidebar' | 'footer'
 *  - format: 'auto' (default) | 'rectangle' | 'horizontal' | 'vertical'
 *  - layout: opcjonalne data-ad-layout (np. 'in-article')
 *  - className, style: appendowane do <ins>
 *
 * Cache settings z /api/settings/seo wspoldzielimy modulowo zeby nie fetchowac per-slot.
 */

let _settingsPromise = null;

function fetchSettingsOnce() {
    if (_settingsPromise) return _settingsPromise;
    _settingsPromise = fetch(`${API_URL}/api/settings/seo`)
        .then(r => r.ok ? r.json() : null)
        .catch(() => null);
    return _settingsPromise;
}

export function AdSlot({ name, format = 'auto', layout, className = '', style = {} }) {
    const consent = useConsent();
    const insRef = useRef(null);
    const [cfg, setCfg] = useState(null);

    useEffect(() => {
        let aborted = false;
        fetchSettingsOnce().then(s => {
            if (!aborted) setCfg(s);
        });
        return () => { aborted = true; };
    }, []);

    const adsense = cfg?.adsense;
    const client = adsense?.client_id;
    const slot = adsense?.slots?.[name];
    const enabled = !!(
        consent?.ads &&
        adsense?.enabled_global &&
        slot?.enabled &&
        slot?.id &&
        client
    );

    useEffect(() => {
        if (!enabled) return;
        if (!insRef.current) return;
        try {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (e) {
            // adsbygoogle moze jeszcze sie nie zaladowac; SEOHead ladowanie skryptu po consent
            // odpali sie tuz po nim; nie spamujemy konsoli
        }
    }, [enabled]);

    if (!enabled) {
        // Cichy placeholder - ulatwia debug w dev (data-attr widoczne w DOM)
        if (process.env.NODE_ENV !== 'production') {
            return (
                <div
                    data-adslot={name}
                    data-adslot-disabled="1"
                    className={className}
                    style={{ minHeight: 0, ...style }}
                />
            );
        }
        return null;
    }

    const dataAttrs = {
        'data-ad-client': client,
        'data-ad-slot': slot.id,
        'data-ad-format': format,
        'data-full-width-responsive': 'true',
    };
    if (layout) dataAttrs['data-ad-layout'] = layout;

    return (
        <ins
            ref={insRef}
            className={`adsbygoogle ${className}`}
            style={{ display: 'block', ...style }}
            {...dataAttrs}
        />
    );
}

export default AdSlot;
